import * as React from "react";
import { useMappedState } from "redux-react-hook";
import * as _ from "lodash";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import {
  deliveryDateHasPassed,
  isFinalStatus,
  orderIsLockedByStatus
} from "src/core/orders/utils/orders";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import {
  IOrder,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import { useDaysToCutoff } from "src/core/orders/hooks/validation";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

import useOrderIsBeforeCutoff from "./useOrderIsBeforeCutoff";

/**
 * Check if the order passes validation
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return {string | null} - The reason it didn't pass, or null if it does pass.
 */
const useCanCancelOrder = (): typeof canCancelOrder => {
  const orderIsBeforeCutOff = useOrderIsBeforeCutoff();
  const [ { drop } ] = useDrop();

  const {
    role, erp, rjlDeliveryNumbers, paymentSettings
  } = useMappedState(({ global }: IStore) => ({
    role: global.role,
    erp: global.settings && global.settings.erp.name,
    rjlDeliveryNumbers: global.settings?.rjlDeliveryNumbers,
    paymentSettings: global.settings?.payment
  }));

  const [ { globalSettings } ] = useGlobalSettings();
  const daysToCutoff = useDaysToCutoff();

  const canCancelOrder = React.useCallback(
    (order: IOrder): string | null => {
      if (!drop) {
        return "Drop not found!";
      }

      if (!role) {
        return "No user role set!";
      }

      if (order.orderStatus && order.orderStatus.settings.group === "CANCELLED") {
        return "Order is already cancelled";
      }

      if (order.orderStatus && isFinalStatus(order.orderStatus)) {
        return "Order cannot be amended, as it is in final status";
      }

      if (order.orderStatus && orderIsLockedByStatus(order.orderStatus)) {
        return `Cannot edit orders in a status of ${order.orderStatus.name}`;
      }

      if (!paymentSettings?.canCancel && [ OrderPaymentStatus.CAPTURED, OrderPaymentStatus.UNCAPTURED ].includes(order.paymentStatus as OrderPaymentStatus)) {
        return "An order cannot be changed after payment has been taken";
      }

      // check the rjl delivery number validates correctly
      if (rjlDeliveryNumbers && order.rjlDeliveryNumber && order.orderDeliveryDate) {
        const orderDeliveryNumber = _.find(rjlDeliveryNumbers, dn => dn.number === order.rjlDeliveryNumber);

        // if theres a min lead time then validate the cutoff of the order on this not the order cutoff
        if (orderDeliveryNumber?.options?.minLeadTimeGroup) {
          const minLeadTimeGroup = _.find(globalSettings?.leadTimeGroups, ltg => ltg.name === orderDeliveryNumber?.options?.minLeadTimeGroup);

          // shouldnt ever happen return a string to block editing though
          if (!minLeadTimeGroup) {
            return "Could not find matching lead time group";
          }
          const minLeadTimeCutoff = daysToCutoff(order.orderDeliveryDate, minLeadTimeGroup);

          if (minLeadTimeCutoff === null || minLeadTimeCutoff < 0) {
            return "Order cannot be submitted, as cutoff has passed";
          }
        }
      }

      if (role.type === "admin" && (!order.orderReference || erp === "seedless")) {
        return null;
      }

      if (!drop) {
        return "Order cannot be cancelled.";
      }

      const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

      if (!leadTimes || !defaultCutoff) {
        return "Order cannot be cancelled.";
      }

      // Only do delivery date validation if it's a date-led order
      if (order.orderDeliveryDate) {
        // if the delivery date is in the past then - FAIL
        if (deliveryDateHasPassed(order.orderDeliveryDate)) {
          return "Order cannot be cancelled, as delivery date is in the past";
        }

        if (orderIsBeforeCutOff(order, "shortest")) {
          return "Order cannot be cancelled, as cutoff has passed";
        }
      }

      return null;
    },
    [
      drop,
      erp,
      orderIsBeforeCutOff,
      role
    ]
  );

  return canCancelOrder;
};

export default useCanCancelOrder;
